import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Switch,
  Button,
  ButtonGroup,
  InputAdornment,
  IconButton,
  FormControl,
  Collapse,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import { Event } from "@mui/icons-material";
import { getDate, format } from "date-fns";
import TextField from "components/common/TextField";
import Radio from "components/common/Radio";
import { ChevronUp, ChevronDown } from "lucide-react";
import { pluralize } from "../../common/utils";

export type RecurrenceEnd = "never" | "on" | "after";
export type MonthlyPattern = "dayOfMonth" | "dayOfWeek";

export interface RecurrenceData {
  interval: number;
  unit: "day" | "week" | "month" | "year";
  byDay: number[]; // 0-6 for Sun-Sat
  monthlyPattern?: MonthlyPattern;
  endType: RecurrenceEnd;
  endDate?: Date;
  count?: number;
}

interface CustomRecurrenceProps {
  onChange: (data: RecurrenceData | null) => void;
  initialData?: RecurrenceData;
  startDate?: Date;
}

const WEEKDAYS = [
  { label: "Su", value: 0 },
  { label: "Mo", value: 1 },
  { label: "Tu", value: 2 },
  { label: "We", value: 3 },
  { label: "Th", value: 4 },
  { label: "Fr", value: 5 },
  { label: "Sa", value: 6 },
];

const UNITS = [
  { label: "day", value: "day" },
  { label: "week", value: "week" },
  { label: "month", value: "month" },
  { label: "year", value: "year" },
];

export default function CustomRecurrence({
  onChange,
  initialData,
  startDate = new Date(),
}: CustomRecurrenceProps) {
  const theme = useTheme();
  const [repeatsEnabled, setRepeatsEnabled] = useState(!!initialData);
  const [repeatEndsEnabled, setRepeatEndsEnabled] = useState(
    initialData?.endType !== "never" && initialData?.endType !== undefined,
  );
  const [interval, setInterval] = useState(initialData?.interval || 1);
  const [unit, setUnit] = useState<"day" | "week" | "month" | "year">(
    initialData?.unit || "week",
  );
  const [selectedDays, setSelectedDays] = useState<number[]>(
    initialData?.byDay || [1], // Default to Monday
  );
  const [monthlyPattern, setMonthlyPattern] = useState<MonthlyPattern>(
    initialData?.monthlyPattern || "dayOfMonth",
  );
  const [endType, setEndType] = useState<RecurrenceEnd>(
    initialData?.endType || "on",
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    initialData?.endDate,
  );
  const [occurrenceCount, setOccurrenceCount] = useState<number>(
    initialData?.count || 13,
  );
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Auto-open date picker when switching to "on" with no date
  useEffect(() => {
    if (repeatEndsEnabled && endType === "on" && !endDate) {
      setDatePickerOpen(true);
    }
  }, [repeatEndsEnabled, endType, endDate]);

  const getWeekdayOccurrence = (date: Date): number => {
    const dayOfMonth = getDate(date);
    return Math.ceil(dayOfMonth / 7);
  };

  const getOrdinalSuffix = (n: number): string => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  };

  const getMonthlyPatternLabel = (
    pattern: MonthlyPattern,
    date: Date,
  ): string => {
    if (pattern === "dayOfMonth") {
      const day = getDate(date);
      return `the ${day}${getOrdinalSuffix(day)}`;
    } else {
      const occurrence = getWeekdayOccurrence(date);
      const weekdayName = format(date, "EEEE");
      return `${occurrence}${getOrdinalSuffix(occurrence)} ${weekdayName}`;
    }
  };

  const handleDayToggle = (day: number) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter((d) => d !== day));
    } else {
      setSelectedDays([...selectedDays, day].sort());
    }
  };

  useEffect(() => {
    if (!repeatsEnabled) {
      onChangeRef.current(null);
      return;
    }

    const data: RecurrenceData = {
      interval,
      unit,
      byDay: unit === "week" ? selectedDays : [],
      monthlyPattern: unit === "month" ? monthlyPattern : undefined,
      endType: repeatEndsEnabled ? endType : "never",
      endDate: repeatEndsEnabled && endType === "on" ? endDate : undefined,
      count:
        repeatEndsEnabled && endType === "after" ? occurrenceCount : undefined,
    };
    onChangeRef.current(data);
  }, [
    repeatsEnabled,
    repeatEndsEnabled,
    interval,
    unit,
    selectedDays,
    monthlyPattern,
    endType,
    endDate,
    occurrenceCount,
  ]);

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {/* Repeats Toggle */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="body"
          color="text.primary"
          sx={{ fontWeight: 600 }}
        >
          Repeats
        </Typography>
        <Switch
          sx={{
            margin: 0,
          }}
          checked={repeatsEnabled}
          onChange={(e) => setRepeatsEnabled(e.target.checked)}
          size="medium"
        />
      </Box>

      <Collapse in={repeatsEnabled}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            paddingTop: repeatsEnabled ? "16px" : 0,
          }}
        >
          {/* Every section */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              variant="body"
              color="text.primary"
              sx={{ fontWeight: 600 }}
            >
              Every
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <TextField
                variant="detachedOutlined"
                type="number"
                value={interval}
                size="small"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setInterval(Math.max(1, parseInt(e.target.value) || 1))
                }
                inputProps={{ min: 1, max: 999 }}
                sx={{ width: "88px" }}
              />
              <ButtonGroup fullWidth>
                {UNITS.map((u) => (
                  <Button
                    key={u.value}
                    variant={
                      unit === u.value ? "buttonGroupSelected" : "buttonGroup"
                    }
                    onClick={() => setUnit(u.value as typeof unit)}
                  >
                    {u.label.charAt(0).toUpperCase() + u.label.slice(1)}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>

          {/* On section (only for weekly) */}
          {unit === "week" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Typography
                variant="body"
                color="text.primary"
                sx={{ fontWeight: 600 }}
              >
                On
              </Typography>
              <Box sx={{ display: "flex", gap: "8px" }}>
                {WEEKDAYS.map((day) => (
                  <Button
                    size="small"
                    key={day.value}
                    onClick={() => handleDayToggle(day.value)}
                    sx={{
                      backgroundColor: selectedDays.includes(day.value)
                        ? theme.palette.primary.main
                        : "#F6F6F6",
                    }}
                  >
                    <Typography
                      variant="body"
                      sx={{
                        color: selectedDays.includes(day.value)
                          ? "#fff"
                          : "#000",
                      }}
                    >
                      {day.label}
                    </Typography>
                  </Button>
                ))}
              </Box>
            </Box>
          )}

          {/* On section (only for monthly) */}
          {unit === "month" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Typography variant="body" color="text.primary">
                On
              </Typography>
              <Box sx={{ display: "flex", gap: "8px" }}>
                <Button
                  onClick={() => setMonthlyPattern("dayOfMonth")}
                  sx={{
                    flex: 1,
                    backgroundColor:
                      monthlyPattern === "dayOfMonth"
                        ? theme.palette.primary.main
                        : "#F6F6F6",
                  }}
                >
                  <Typography
                    variant="body"
                    sx={{
                      color: monthlyPattern === "dayOfMonth" ? "#fff" : "#000",
                    }}
                  >
                    {getMonthlyPatternLabel("dayOfMonth", startDate)}
                  </Typography>
                </Button>
                <Button
                  onClick={() => setMonthlyPattern("dayOfWeek")}
                  sx={{
                    flex: 1,
                    backgroundColor:
                      monthlyPattern === "dayOfWeek"
                        ? theme.palette.primary.main
                        : "#F6F6F6",
                  }}
                >
                  <Typography
                    variant="body"
                    sx={{
                      color: monthlyPattern === "dayOfWeek" ? "#fff" : "#000",
                    }}
                  >
                    {getMonthlyPatternLabel("dayOfWeek", startDate)}
                  </Typography>
                </Button>
              </Box>
            </Box>
          )}

          {/* Repeat ends Toggle */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="body"
              color="text.primary"
              sx={{ fontWeight: 600 }}
            >
              Repeat ends
            </Typography>
            <Switch
              checked={repeatEndsEnabled}
              onChange={(e) => setRepeatEndsEnabled(e.target.checked)}
              size="medium"
            />
          </Box>

          <Collapse in={repeatEndsEnabled}>
            <Box sx={{ paddingTop: repeatEndsEnabled ? "16px" : 0 }}>
              <RadioGroup
                value={endType}
                onChange={(e) => setEndType(e.target.value as RecurrenceEnd)}
                sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <FormControlLabel
                    value="on"
                    control={<Radio />}
                    label="On"
                    sx={{ width: "80px" }}
                  />
                  <FormControl>
                    <DatePicker
                      value={endDate}
                      disabled={endType !== "on"}
                      open={datePickerOpen}
                      onClose={() => setDatePickerOpen(false)}
                      onChange={(newDate) => {
                        if (newDate) {
                          setEndDate(newDate);
                        }
                      }}
                      minDate={startDate}
                      format="MM/dd/yyyy"
                      sx={{
                        ".MuiInputBase-input": {
                          marginTop: "0",
                        },
                        ".MuiInputBase-root": {
                          minHeight: "0",
                        },
                        "& .MuiInputBase-root": {
                          height: "48px",
                          fontSize: "12px",
                          padding: "0 20px 0 8px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 8px",
                        },
                        width: "180px",
                      }}
                      slotProps={{
                        textField: {
                          disabled: endType !== "on",
                          onClick: (e) => {
                            if (endType === "on") {
                              e.stopPropagation();
                              setDatePickerOpen(true);
                              setTimeout(() => {
                                (e.target as HTMLInputElement).focus();
                              });
                            }
                          },
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  edge="end"
                                  onClick={() => {
                                    if (endType === "on") {
                                      setDatePickerOpen(true);
                                    }
                                  }}
                                >
                                  <Event />
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        },
                      }}
                    />
                  </FormControl>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <FormControlLabel
                    value="after"
                    control={<Radio />}
                    label="After"
                    sx={{ width: "80px" }}
                  />
                  <TextField
                    type="number"
                    variant="detachedOutlined"
                    size="small"
                    value={occurrenceCount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setOccurrenceCount(
                        Math.max(1, parseInt(e.target.value) || 1),
                      )
                    }
                    disabled={endType !== "after"}
                    inputProps={{ min: 1, max: 999 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography
                            variant="body"
                            sx={{
                              color: endType !== "after" ? "#999" : "#000",
                            }}
                          >
                            {pluralize("occurrence", occurrenceCount)}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              marginLeft: "8px",
                            }}
                          >
                            <ChevronUp
                              size={16}
                              onClick={() =>
                                endType === "after" &&
                                setOccurrenceCount(
                                  Math.min(999, occurrenceCount + 1),
                                )
                              }
                              style={{
                                color: endType !== "after" ? "#999" : "#000",
                                cursor:
                                  endType !== "after" ? "default" : "pointer",
                              }}
                            />
                            <ChevronDown
                              size={16}
                              onClick={() =>
                                endType === "after" &&
                                setOccurrenceCount(
                                  Math.max(1, occurrenceCount - 1),
                                )
                              }
                              style={{
                                color: endType !== "after" ? "#999" : "#000",
                                cursor:
                                  endType !== "after" ? "default" : "pointer",
                              }}
                            />
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: "180px",
                      "& input[type=number]": {
                        MozAppearance: "textfield",
                      },
                      "& input[type=number]::-webkit-outer-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                      "& input[type=number]::-webkit-inner-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                    }}
                  />
                </Box>
              </RadioGroup>
            </Box>
          </Collapse>
        </Box>
      </Collapse>
    </Box>
  );
}
